import { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faMapMarkerAlt, faCheckCircle,
  faUser, faMapPin, faLightbulb, faClipboardCheck,
  faChevronLeft, faChevronRight, faPlusCircle, faExclamationTriangle,
  faDownload, faShareAlt, faCopy, faCheck,
  faCamera, faImages, faUpload, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { publicRequestService, ApiError } from '../../services/api';
import { uploadPhoto, isImageFile, MAX_PHOTO_SIZE_BYTES } from '../../services/cloudinary';
import { downloadPublicReceipt, statusToLabel } from '../../services/receiptPdf';
import type { LampCondition } from '../../types/publicRequest';
import { LAMP_CONDITION_LABELS } from '../../types/publicRequest';
import { isValidCPF } from './cpfValidator';
import './NewRequest.css';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAADi1mqWEGyGXPcDO';

/** True quando rodando em localhost — Turnstile não valida esse domínio (erro 110200). */
const IS_LOCALHOST =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/** Detecta dispositivo móvel (touch) para decidir entre captura de câmera e upload de arquivo. */
function isMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  const touch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
  const uaMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(navigator.userAgent);
  return coarse || (touch && uaMobile);
}

const STORAGE_KEY = 'iluminacao_publica_perfil';

interface SavedProfile {
  reporterName: string;
  email: string;
  whatsapp: string;
  birthDate: string; // YYYY-MM-DD para compatibilidade com backend
}

function loadProfile(cpfDigits: string): SavedProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const all: Record<string, SavedProfile> = JSON.parse(raw);
    return all[cpfDigits] || null;
  } catch {
    return null;
  }
}

function saveProfile(cpfDigits: string, profile: SavedProfile) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const all: Record<string, SavedProfile> = raw ? JSON.parse(raw) : {};
    all[cpfDigits] = profile;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch { /* silencia no limite de quota */ }
}

// ── Conversão de data: DD/MM/AAAA ↔ YYYY-MM-DD ────────────────────

/** Formato de exibição (máscara) → ISO (backend/localStorage). */
function birthDateToISO(display: string): string {
  const d = display.replace(/\D/g, '');
  if (d.length === 8) return `${d.slice(4, 8)}-${d.slice(2, 4)}-${d.slice(0, 2)}`;
  return d; // parcial — o backend validará quando completo
}

/** ISO (backend/localStorage) → formato de exibição DD/MM/AAAA. */
function birthDateToDisplay(iso: string): string {
  if (iso.length === 10) {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
  }
  return iso;
}

/** Calcula idade a partir de uma data em formato DD/MM/AAAA (10 chars). */
function computeAge(displayDate: string): number | null {
  if (displayDate.length !== 10) return null;
  const d = displayDate.replace(/\D/g, '');
  if (d.length !== 8) return null;
  const day = +d.slice(0, 2);
  const month = +d.slice(2, 4);
  const year = +d.slice(4, 8);
  const birth = new Date(year, month - 1, day);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const hadBday = today.getMonth() > birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
  if (!hadBday) age--;
  return age;
}

// ── Máscaras de input ──────────────────────────────────────────────

function applyCpfMask(raw: string): string {
  const val = raw.replace(/\D/g, '').slice(0, 11);
  if (val.length > 9) return `${val.slice(0, 3)}.${val.slice(3, 6)}.${val.slice(6, 9)}-${val.slice(9)}`;
  if (val.length > 6) return `${val.slice(0, 3)}.${val.slice(3, 6)}.${val.slice(6)}`;
  if (val.length > 3) return `${val.slice(0, 3)}.${val.slice(3)}`;
  return val;
}

function applyBirthDateMask(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  if (d.length >= 5) return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4, 8)}`;
  if (d.length >= 3) return `${d.slice(0, 2)}/${d.slice(2, 4)}`;
  if (d.length >= 1) return d.slice(0, 2);
  return '';
}

function applyWhatsappMask(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 11);
  if (d.length >= 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
  if (d.length >= 7) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length >= 3) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length >= 1) return `(${d}`;
  return '';
}

// ────────────────────────────────────────────────────────────────────

const BAIRROS = [
  'Aldeia Cadrogó',
  'Aldeia Caípe',
  'Aldeia Canaã',
  'Aldeia Dois Irmãos',
  'Aldeia Lagoa',
  'Aldeia Santana',
  'Aldeia São José',
  'Aldeia Varzinha',
  'Assent. Nossa Senhora',
  'Baixa Grande',
  'Beira Mar',
  'Cacimbão',
  "Caixa D'Água",
  'Caldeirão I',
  'Caldeirão II',
  'Caldeirão III',
  'Central',
  'Centenário',
  'Centro',
  'Cimbres',
  'Climério',
  'Cohab I',
  'Cohab II',
  'Eucalipto',
  'Fazenda Rosário',
  'Freixeira Velha',
  'Ipanema',
  'Lagoa do Félix',
  'Lot. Biá',
  'Lot. Gravatazinho',
  'Lot. JK',
  'Lot. José Gerônimo',
  'Lot. José Rocha',
  'Lot. Leonardo Gomes',
  'Lot. Miguel Lins',
  'Lot. Redenção',
  'Milho Branco',
  'Mimoso',
  'Mutuca',
  'Novo Cajueiro',
  'Papagaio de Baixo',
  'Papagaio de Cima',
  'Papagaio do Meio',
  'Pedra Redonda',
  'Pintada',
  'Pitanga',
  'Pitanguinha',
  'Portal',
  'Prado',
  'Riacho Fundo',
  'Salgado',
  'Salobro',
  'Santo Antônio',
  'São Sebastião',
  'Serra da Cruz',
  'Serrinha',
  'T.Q. Negros do Osso',
  'Triângulo I',
  'Triângulo II',
  'Triângulo III',
  'Vira Kopus',
  'Vila Anápolis',
  'Vila do Presídio',
  'Xucurus',
];

type Step = 1 | 2 | 3 | 4;

const STEP_LABELS: Record<Step, { title: string; icon: typeof faUser }> = {
  1: { title: 'Identificação', icon: faUser },
  2: { title: 'Localização', icon: faMapPin },
  3: { title: 'O problema', icon: faLightbulb },
  4: { title: 'Revisão', icon: faClipboardCheck },
};

export default function NewRequest() {
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [receiptData, setReceiptData] = useState<{ protocol: string; address: string; bairro: string; busbar: string; openedAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [duplicateProtocol, setDuplicateProtocol] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const turnstileRef = useRef<string | null>(null);
  const turnstileWidgetId = useRef<string | undefined>(undefined);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [turnstileReady, setTurnstileReady] = useState(false);

  const [form, setForm] = useState({
    reporterName: '',
    cpf: '',
    poleBusbar: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: 'Centro',
    referencePoint: '',
    lampCondition: 'APAGADA' as LampCondition,
    birthDate: '',   // exibição: DD/MM/AAAA
    email: '',
    whatsapp: '',    // exibição: (87) 99999-0000
    description: '',
  });
  const [isAdult, setIsAdult] = useState(false);
  const [dataIsTrue, setDataIsTrue] = useState(false);
  const [acceptsPrivacyPolicy, setAcceptsPrivacyPolicy] = useState(false);
  const [noBusbar, setNoBusbar] = useState(false);
  const [fieldError, setFieldError] = useState<{ id: string; msg: string } | null>(null);
  const [isMobile] = useState(() => isMobileDevice());
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  // ── helpers ──────────────────────────────────────────────────────────

  const update = (patch: Partial<typeof form>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const clearFieldError = (fieldId: string) => {
    setFieldError((prev) => (prev?.id === fieldId ? null : prev));
  };

  const scrollToField = (fieldId: string) => {
    setTimeout(() => {
      const el = document.getElementById(fieldId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus();
      }
    }, 50);
  };

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied((c) => (c === value ? null : c)), 2000);
    } catch { /* clipboard indisponível */ }
  };

  const goNext = () => { setError(null); setFieldError(null); scrollTop(); setStep((s) => Math.min(4, s + 1) as Step); };
  const goPrev = () => { setError(null); setFieldError(null); scrollTop(); setStep((s) => Math.max(1, s - 1) as Step); };

  // ── CPF mask + auto-fill ─────────────────────────────────────────────

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyCpfMask(e.target.value);
    update({ cpf: masked });
    clearFieldError('field-cpf');

    const digits = masked.replace(/\D/g, '');
    if (digits.length === 11 && isValidCPF(masked)) {
      const saved = loadProfile(digits);
      if (saved) {
        update({
          reporterName: saved.reporterName,
          email: saved.email,
          whatsapp: applyWhatsappMask(saved.whatsapp),
          birthDate: birthDateToDisplay(saved.birthDate),
        });
        const age = computeAge(birthDateToDisplay(saved.birthDate));
        setIsAdult(age !== null && age >= 18);
      }
    }
  };

  // ── Birth date mask + age check ─────────────────────────────────────

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const display = applyBirthDateMask(e.target.value);
    update({ birthDate: display });
    const age = computeAge(display);
    setIsAdult(age !== null && age >= 18);
    clearFieldError('field-birthDate');
  };

  // ── WhatsApp mask ────────────────────────────────────────────────────

  const handleWhatsappChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    update({ whatsapp: applyWhatsappMask(e.target.value) });
    clearFieldError('field-whatsapp');
  };

  // ── Busbar mask ──────────────────────────────────────────────────────

  const handleBusbarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    val = val.replace(/^(\d)/, '');             // nao permite comecar com digito
    val = val.replace(/([A-Z]).*/, (_, c) => c + val.slice(1).replace(/[A-Z]/g, ''));
    val = val.slice(0, 8);
    update({ poleBusbar: val });
    clearFieldError('field-poleBusbar');
  };

  // ── Geolocation ──────────────────────────────────────────────────────

  const captureLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Seu navegador não suporta geolocalização.');
      return;
    }
    setLocating(true);
    setError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        // GeolocationPositionError: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        if (err.code === 1) {
          setError('Permita o acesso à localização nas configurações do navegador.');
        } else if (err.code === 2) {
          setError('Sinal GPS indisponível. Tente em área aberta.');
        } else if (err.code === 3) {
          setError('Tempo esgotado. Aproxime-se de uma janela ou área externa e tente novamente.');
        } else {
          setError('Não foi possível obter a localização. Verifique se o GPS está ativado.');
        }
      },
      { enableHighAccuracy: true, timeout: 30000, maximumAge: 60000 },
    );
  }, []);

  // ── Foto (opcional) ────────────────────────────────────────────────

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isImageFile(file)) {
      setError('O arquivo selecionado não é uma imagem.');
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      setError('A foto deve ter no máximo 5 MB.');
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const removePhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  // ── Validation ───────────────────────────────────────────────────────

  type FieldValidation = { error: string; fieldId: string };

  const validateStep1 = (): FieldValidation | null => {
    if (!form.reporterName.trim()) return { error: 'Informe seu nome completo.', fieldId: 'field-reporterName' };
    if (!isValidCPF(form.cpf)) return { error: 'CPF inválido.', fieldId: 'field-cpf' };
    if (form.birthDate.length < 10) return { error: 'Data de nascimento obrigatória.', fieldId: 'field-birthDate' };
    if (!isAdult) return { error: 'É necessário ser maior de 18 anos para abrir uma solicitação.', fieldId: 'field-birthDate' };
    if (form.whatsapp.replace(/\D/g, '').length < 10) return { error: 'WhatsApp inválido.', fieldId: 'field-whatsapp' };
    if (!form.email.trim() || !form.email.includes('@')) return { error: 'E-mail válido é obrigatório.', fieldId: 'field-email' };
    return null;
  };

  const validateStep2 = (): FieldValidation | null => {
    if (!form.logradouro.trim()) return { error: 'Informe o logradouro.', fieldId: 'field-logradouro' };
    if (!form.numero.trim()) return { error: 'Informe o número.', fieldId: 'field-numero' };
    return null;
  };

  const validateStep3 = (): FieldValidation | null => {
    if (noBusbar) return null;
    const val = form.poleBusbar.toUpperCase().trim();
    if (!val) return { error: 'Barramento é obrigatório.', fieldId: 'field-poleBusbar' };
    if (val === 'S/B') return null;
    if (!/^[A-Z]\d{1,7}$/.test(val))
      return { error: 'Barramento deve seguir o formato de uma letra (A-Z) + 1 a 7 números (ex: A1234567).', fieldId: 'field-poleBusbar' };
    return null;
  };

  const validateAll = (): FieldValidation | null => validateStep1() || validateStep2() || validateStep3();

  const handleNext = () => {
    let res: FieldValidation | null = null;
    if (step === 1) res = validateStep1();
    if (step === 2) res = validateStep2();
    if (step === 3) res = validateStep3();
    if (res) {
      setError(res.error);
      setFieldError({ id: res.fieldId, msg: res.error });
      scrollToField(res.fieldId);
      return;
    }
    if (step < 4) goNext();
  };

  // ── Submit ───────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDuplicateProtocol(null);

    const err = validateAll();
    if (err) { setError(err.error); return; }
    if (!dataIsTrue) { setError('Declare que as informações são verídicas.'); return; }
    if (!acceptsPrivacyPolicy) { setError('Aceite a política de privacidade.'); return; }
    if (!IS_LOCALHOST && !turnstileRef.current) { setError('Verificação anti-robô pendente.'); return; }

    setLoading(true);
    try {
      const lat = location ? location.lat : 0;
      const lng = location ? location.lng : 0;
      const cpfDigits = form.cpf.replace(/\D/g, '');

      let photoUrl: string | undefined;
      if (photoFile) {
        setPhotoUploading(true);
        try {
          photoUrl = await uploadPhoto(photoFile);
        } catch (uploadErr: unknown) {
          const msg = uploadErr instanceof Error ? uploadErr.message : 'Falha ao enviar a foto.';
          setError(msg);
          return;
        } finally {
          setPhotoUploading(false);
        }
      }

      const res = await publicRequestService.create({
        reporterName: form.reporterName.trim(),
        cpf: cpfDigits,
        birthDate: birthDateToISO(form.birthDate),
        email: form.email.trim(),
        whatsapp: form.whatsapp.replace(/\D/g, ''),
        poleBusbar: noBusbar ? 'S/B' : form.poleBusbar.toUpperCase().trim(),
        logradouro: form.logradouro.trim(),
        numero: form.numero.trim(),
        complemento: form.complemento.trim() || undefined,
        bairro: form.bairro,
        referencePoint: form.referencePoint.trim() || undefined,
        lampCondition: form.lampCondition,
        latitude: lat,
        longitude: lng,
        description: form.description.trim() || undefined,
        photoUrl,
        turnstileToken: IS_LOCALHOST ? 'dev-bypass' : (turnstileRef.current ?? ''),
        isAdult,
        dataIsTrue,
        acceptsPrivacyPolicy,
      });

      // Salva perfil no localStorage (nome, e-mail, WhatsApp puro, data ISO)
      saveProfile(cpfDigits, {
        reporterName: form.reporterName.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.replace(/\D/g, ''),
        birthDate: birthDateToISO(form.birthDate),
      });

      setSuccess(res.protocolNumber);
      setReceiptData({
        protocol: res.protocolNumber,
        address: `${form.logradouro.trim()}, ${form.numero.trim()}${form.complemento ? ` - ${form.complemento.trim()}` : ''}`,
        bairro: form.bairro,
        busbar: noBusbar ? 'S/B' : form.poleBusbar.toUpperCase().trim(),
        openedAt: new Date().toISOString(),
      });
      setForm({
        reporterName: '', cpf: '', poleBusbar: '', logradouro: '',
        numero: '', complemento: '', bairro: 'Centro', referencePoint: '',
        lampCondition: 'APAGADA', birthDate: '', email: '', whatsapp: '', description: '',
      });
      setLocation(null);
      setIsAdult(false);
      setDataIsTrue(false);
      setAcceptsPrivacyPolicy(false);
      setNoBusbar(false);
      if (photoPreview) URL.revokeObjectURL(photoPreview);
      setPhotoFile(null);
      setPhotoPreview(null);
      setStep(1);
      turnstileRef.current = null;
      window.turnstile?.reset(turnstileWidgetId.current ?? undefined);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Falha ao enviar solicitação.';
      setError(msg);
      if (err instanceof ApiError && err.protocolNumber) {
        setDuplicateProtocol(err.protocolNumber);
      }
      window.turnstile?.reset(turnstileWidgetId.current ?? undefined);
      turnstileRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  // ── Turnstile ────────────────────────────────────────────────────────

  useEffect(() => {
    if (IS_LOCALHOST || !TURNSTILE_SITE_KEY) return;
    if (window.turnstile) { setTurnstileReady(true); return; }
    if (document.querySelector('script[data-turnstile]')) return;
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    s.async = true;
    s.dataset.turnstile = '1';
    s.onload = () => setTurnstileReady(true);
    document.head.appendChild(s);
  }, []);

  const renderTurnstile = useCallback((el: HTMLDivElement | null) => {
    if (!el || !TURNSTILE_SITE_KEY || !turnstileReady) return;
    turnstileWidgetId.current = window.turnstile?.render(el, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token: string) => { turnstileRef.current = token; },
      'error-callback': () => { turnstileRef.current = null; },
    });
  }, [turnstileReady]);

  useEffect(() => {
    if (step !== 4) return;
    const t = setTimeout(() => {
      window.turnstile?.reset(turnstileWidgetId.current ?? undefined);
      turnstileRef.current = null;
    }, 100);
    return () => clearTimeout(t);
  }, [step]);

  // ── Success screen ───────────────────────────────────────────────────

  if (success) {
    const handleDownload = () => {
      if (!receiptData) return;
      downloadPublicReceipt({
        protocolNumber: receiptData.protocol,
        address: receiptData.address,
        neighborhood: receiptData.bairro,
        poleBusbar: receiptData.busbar,
        status: 'ABERTA',
        statusLabel: statusToLabel('ABERTA'),
        openedAt: receiptData.openedAt,
      });
    };

    const handleShare = async () => {
      if (!receiptData) return;
      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Comprovante de Abertura — Iluminação Pública',
            text: `Protocolo: ${receiptData.protocol}\nEndereço: ${receiptData.address}\nBarramento: ${receiptData.busbar}\nAcompanhe em: https://iluminacaopublica.pesqueira.pe.gov.br/consultar`,
          });
        } catch { /* usuário cancelou */ }
      } else {
        handleDownload();
      }
    };

    return (
      <div className="card wizard-card success-card">
        <div className="success-icon">
          <FontAwesomeIcon icon={faCheckCircle} />
        </div>
        <h2 style={{ marginBottom: 8, color: 'var(--color-text-dark)' }}>Solicitação enviada!</h2>
        <p style={{ margin: '0 0 16px', color: 'var(--color-text-muted)', fontSize: 14 }}>
          Sua solicitação foi recebida.<br />
          Você também receberá um e-mail de confirmação.
        </p>
        <div className="success-protocol">
          <span>Número de protocolo</span>
          <strong>{success}</strong>
          <button
            type="button"
            className="copy-btn"
            onClick={() => copyToClipboard(success)}
            aria-label="Copiar número de protocolo"
            title="Copiar número de protocolo"
          >
            <FontAwesomeIcon icon={copied === success ? faCheck : faCopy} />
            {copied === success ? 'Copiado!' : 'Copiar'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleDownload} style={{ flex: 1 }}>
            <FontAwesomeIcon icon={faDownload} /> Baixar comprovante
          </button>
          <button className="btn btn-accent" onClick={handleShare} style={{ flex: 1 }}>
            <FontAwesomeIcon icon={faShareAlt} /> Compartilhar
          </button>
        </div>

        <button className="btn btn-secondary btn-block" onClick={() => { setSuccess(null); setReceiptData(null); }} style={{ marginTop: 10 }}>
          <FontAwesomeIcon icon={faPlusCircle} /> Nova solicitação
        </button>
      </div>
    );
  }

  const StepIcon = STEP_LABELS[step].icon;
  const totalSteps = 4;

  return (
    <div className="wizard-card card">
      {/* Step indicator */}
      <div className="wizard-steps">
        {([1, 2, 3, 4] as Step[]).map((s) => {
          const st = STEP_LABELS[s];
          const done = s < step;
          const active = s === step;
          return (
            <div key={s} className={`wizard-step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
              <div className="wizard-step-dot">
                <FontAwesomeIcon icon={active ? StepIcon : st.icon} />
              </div>
              <span className="wizard-step-label">{st.title}</span>
            </div>
          );
        })}
      </div>

      <h2 className="wizard-title">
        <FontAwesomeIcon icon={StepIcon} style={{ marginRight: 10 }} />
        {STEP_LABELS[step].title}
      </h2>
      <p className="wizard-subtitle">
        {step === 1 && 'Seus dados de contato'}
        {step === 2 && 'Onde fica o poste?'}
        {step === 3 && 'Qual o problema?'}
        {step === 4 && 'Confira os dados antes de enviar'}
      </p>

      {error && (
        <div className="alert alert-error">
          <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          {duplicateProtocol && (
            <div className="duplicate-protocol">
              <span>Protocolo da OS já aberta:</span>
              <strong>{duplicateProtocol}</strong>
              <button
                type="button"
                className="copy-btn"
                onClick={() => copyToClipboard(duplicateProtocol)}
                aria-label="Copiar número de protocolo"
                title="Copiar número de protocolo"
              >
                <FontAwesomeIcon icon={copied === duplicateProtocol ? faCheck : faCopy} />
                {copied === duplicateProtocol ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          )}
        </div>
      )}

      <form onSubmit={step === 4 ? handleSubmit : (e) => { e.preventDefault(); handleNext(); }}>
        {/* ════════════════════════ STEP 1 — Identificação ════════════════════════ */}
        {step === 1 && (
          <>
            <div className="form-group">
              <label>Nome completo *</label>
              <input id="field-reporterName" name="reporterName" value={form.reporterName}
                onChange={(e) => { update({ reporterName: e.target.value }); clearFieldError('field-reporterName'); }}
                className={fieldError?.id === 'field-reporterName' ? 'input-error' : ''}
                placeholder="Seu nome completo" maxLength={100} autoFocus />
              {fieldError?.id === 'field-reporterName' && <small className="field-error">{fieldError.msg}</small>}
            </div>

            <div className="form-group">
              <label>CPF *</label>
              <input id="field-cpf" name="cpf" value={form.cpf} onChange={handleCpfChange}
                className={fieldError?.id === 'field-cpf' ? 'input-error' : ''}
                placeholder="000.000.000-00" inputMode="numeric" />
              {fieldError?.id === 'field-cpf' && <small className="field-error">{fieldError.msg}</small>}
            </div>

            <div className="form-group">
              <label>Data de nascimento *</label>
              <input id="field-birthDate" name="birthDate" value={form.birthDate} onChange={handleBirthDateChange}
                className={fieldError?.id === 'field-birthDate' ? 'input-error' : ''}
                placeholder="DD/MM/AAAA" inputMode="numeric" maxLength={10} />
              {isAdult && <small style={{ color: 'var(--color-success)' }}>Maior de 18 anos ✓</small>}
              {form.birthDate.length === 10 && !isAdult &&
                <small style={{ color: 'var(--color-danger)' }}>É necessário ter 18+ anos.</small>}
              {fieldError?.id === 'field-birthDate' && <small className="field-error">{fieldError.msg}</small>}
            </div>

            <div className="form-group">
              <label>E-mail *</label>
              <input id="field-email" name="email" type="email" value={form.email}
                onChange={(e) => { update({ email: e.target.value }); clearFieldError('field-email'); }}
                className={fieldError?.id === 'field-email' ? 'input-error' : ''}
                placeholder="seu@email.com" maxLength={100} />
              {fieldError?.id === 'field-email' && <small className="field-error">{fieldError.msg}</small>}
            </div>

            <div className="form-group">
              <label>WhatsApp *</label>
              <input id="field-whatsapp" name="whatsapp" value={form.whatsapp} onChange={handleWhatsappChange}
                className={fieldError?.id === 'field-whatsapp' ? 'input-error' : ''}
                placeholder="(87) 99999-0000" inputMode="tel" maxLength={16} />
              <small style={{ color: 'var(--color-text-muted)' }}>DDD + número com 9 dígitos</small>
              {fieldError?.id === 'field-whatsapp' && <small className="field-error">{fieldError.msg}</small>}
            </div>
          </>
        )}

        {/* ════════════════════════ STEP 2 — Localização ════════════════════════ */}
        {step === 2 && (
          <>
            <div className="location-info">
              <p style={{ marginBottom: 8, color: 'var(--color-text-dark)', fontSize: 14, fontWeight: 600 }}>
                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: 6, color: 'var(--color-primary)' }} />
                Preencha o endereço do poste e capture sua localização via GPS
              </p>
              <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
                Quanto mais detalhes você fornecer, mais rápido sua solicitação será atendida.
              </p>
            </div>

            <fieldset className="location-fieldset">
              <legend><FontAwesomeIcon icon={faMapPin} /> Endereço do poste</legend>

              <div className="form-group">
                <label>Logradouro *</label>
                <input id="field-logradouro" name="logradouro" value={form.logradouro}
                  onChange={(e) => { update({ logradouro: e.target.value }); clearFieldError('field-logradouro'); }}
                  className={fieldError?.id === 'field-logradouro' ? 'input-error' : ''}
                  placeholder="Ex: Rua José de Almeida, Avenida Central..." maxLength={200} autoFocus />
                {fieldError?.id === 'field-logradouro' && <small className="field-error">{fieldError.msg}</small>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Número *</label>
                  <input id="field-numero" name="numero" value={form.numero}
                    onChange={(e) => { update({ numero: e.target.value }); clearFieldError('field-numero'); }}
                    className={fieldError?.id === 'field-numero' ? 'input-error' : ''}
                    placeholder="Nº" maxLength={20} />
                  {fieldError?.id === 'field-numero' && <small className="field-error">{fieldError.msg}</small>}
                </div>
                <div className="form-group">
                  <label>Complemento</label>
                  <input name="complemento" value={form.complemento}
                    onChange={(e) => update({ complemento: e.target.value })}
                    placeholder="Casa, apto, lote..." maxLength={100} />
                </div>
              </div>

              <div className="form-group">
                <label>Bairro *</label>
                <select name="bairro" value={form.bairro}
                  onChange={(e) => update({ bairro: e.target.value })}>
                  {BAIRROS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Ponto de referência</label>
                <input name="referencePoint" value={form.referencePoint}
                  onChange={(e) => update({ referencePoint: e.target.value })}
                  placeholder="Ex: Próximo à praça central, ao lado da padaria..." maxLength={200} />
                <small style={{ color: 'var(--color-text-muted)' }}>
                  Ajude nossa equipe: descreva pontos conhecidos próximos ao poste.
                </small>
              </div>
            </fieldset>

            <div className="gps-section">
              <fieldset className="location-fieldset">
                <legend><FontAwesomeIcon icon={faMapMarkerAlt} /> Localização via GPS <small>(opcional)</small></legend>

                <p style={{ color: 'var(--color-text-muted)', fontSize: 14, marginBottom: 16 }}>
                  Fique próximo ao poste e capture sua localização para ajudar nossa equipe a encontrá-lo mais rápido.
                </p>

                <button type="button" className={`gps-btn ${location ? 'success' : ''}`}
                  onClick={captureLocation} disabled={locating}>
                  <FontAwesomeIcon icon={locating ? faSpinner : faMapMarkerAlt} spin={locating} />
                  {locating ? 'Obtendo sua localização...' : location ? 'Localização capturada ✓' : 'Capturar minha localização'}
                </button>

                {location && (
                  <div className="gps-result" style={{ marginTop: 12 }}>
                    <FontAwesomeIcon icon={faCheckCircle} style={{ color: 'var(--color-success)', marginRight: 8 }} />
                    <span>Lat: {location.lat.toFixed(6)} · Lng: {location.lng.toFixed(6)}</span>
                  </div>
                )}

                {!location && (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 8, textAlign: 'center' }}>
                    Clique no botão acima e permita o acesso à localização quando solicitado.
                  </p>
                )}
              </fieldset>
            </div>
          </>
        )}

        {/* ════════════════════════ STEP 3 — O problema ════════════════════════ */}
        {step === 3 && (
          <>
            <div className="form-group">
              <label>Barramento <small>(ex: A1234567)</small></label>
              <input id="field-poleBusbar" name="poleBusbar" value={form.poleBusbar}
                onChange={handleBusbarChange}
                disabled={noBusbar}
                className={fieldError?.id === 'field-poleBusbar' ? 'input-error' : ''}
                placeholder="Letra A-Z seguida de 1 a 7 dígitos" autoFocus />
              {fieldError?.id === 'field-poleBusbar' && <small className="field-error">{fieldError.msg}</small>}
              <label className="checkbox-inline">
                <input type="checkbox" checked={noBusbar}
                  onChange={(e) => {
                    setNoBusbar(e.target.checked);
                    update({ poleBusbar: e.target.checked ? 'S/B' : '' });
                    clearFieldError('field-poleBusbar');
                  }} />
                Não possui barramento
              </label>
            </div>

            <div className="form-group">
              <label>Condição da lâmpada *</label>
              <select name="lampCondition" value={form.lampCondition}
                onChange={(e) => update({ lampCondition: e.target.value as LampCondition })}>
                {(Object.keys(LAMP_CONDITION_LABELS) as LampCondition[]).map((k) => (
                  <option key={k} value={k}>{LAMP_CONDITION_LABELS[k]}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Observações</label>
              <textarea name="description" value={form.description}
                onChange={(e) => update({ description: e.target.value })}
                placeholder="Descreva o problema com mais detalhes..." rows={3} maxLength={500} />
            </div>

            <div className="photo-section">
              <div className="photo-section-header">
                <FontAwesomeIcon icon={faCamera} />
                <div>
                  <strong>Foto do poste <small>(opcional)</small></strong>
                  <p>Anexe uma foto para ajudar nossa equipe a identificar o problema.</p>
                </div>
              </div>

              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" hidden onChange={handlePhotoChange} />
              <input ref={galleryInputRef} type="file" accept="image/*" hidden onChange={handlePhotoChange} />

              {!photoPreview ? (
                <div className="photo-actions">
                  {isMobile && (
                    <button type="button" className="photo-btn" onClick={() => cameraInputRef.current?.click()}>
                      <FontAwesomeIcon icon={faCamera} /> Tirar foto
                    </button>
                  )}
                  <button type="button" className="photo-btn" onClick={() => galleryInputRef.current?.click()}>
                    <FontAwesomeIcon icon={isMobile ? faImages : faUpload} />
                    {isMobile ? 'Escolher da galeria' : 'Enviar foto do computador'}
                  </button>
                </div>
              ) : (
                <div className="photo-preview">
                  <img src={photoPreview} alt="Prévia da foto do poste" />
                  <div className="photo-preview-actions">
                    <button type="button" className="photo-btn photo-btn-secondary" onClick={() => galleryInputRef.current?.click()}>
                      <FontAwesomeIcon icon={isMobile ? faImages : faUpload} /> Trocar
                    </button>
                    {isMobile && (
                      <button type="button" className="photo-btn photo-btn-secondary" onClick={() => cameraInputRef.current?.click()}>
                        <FontAwesomeIcon icon={faCamera} /> Tirar outra
                      </button>
                    )}
                    <button type="button" className="photo-btn photo-btn-danger" onClick={removePhoto} aria-label="Remover foto" title="Remover foto">
                      <FontAwesomeIcon icon={faTimes} /> Remover
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ════════════════════════ STEP 4 — Revisão ════════════════════════ */}
        {step === 4 && (
          <>
            <div className="summary-block">
              <h4><FontAwesomeIcon icon={faUser} /> Identificação</h4>
              <div className="summary-row"><span>Nome</span><strong>{form.reporterName}</strong></div>
              <div className="summary-row"><span>CPF</span><strong>{form.cpf}</strong></div>
              <div className="summary-row"><span>Nascimento</span><strong>{form.birthDate}</strong></div>
              <div className="summary-row"><span>E-mail</span><strong>{form.email}</strong></div>
              <div className="summary-row"><span>WhatsApp</span><strong>{form.whatsapp}</strong></div>
            </div>

            <div className="summary-block">
              <h4><FontAwesomeIcon icon={faMapPin} /> Localização</h4>
              <div className="summary-row"><span>Endereço</span><strong>{form.logradouro}, {form.numero}{form.complemento ? ` - ${form.complemento}` : ''}</strong></div>
              <div className="summary-row"><span>Bairro</span><strong>{form.bairro}</strong></div>
              {location && (
                <div className="summary-row"><span>Coordenadas</span><strong>{location.lat.toFixed(6)}, {location.lng.toFixed(6)}</strong></div>
              )}
              {form.referencePoint && <div className="summary-row"><span>Ref. ponto</span><strong>{form.referencePoint}</strong></div>}
            </div>

            <div className="summary-block">
              <h4><FontAwesomeIcon icon={faLightbulb} /> Problema</h4>
              <div className="summary-row"><span>Barramento</span><strong>{noBusbar || form.poleBusbar.toUpperCase().trim() === 'S/B' ? 'Sem barramento' : form.poleBusbar.toUpperCase().trim()}</strong></div>
              <div className="summary-row"><span>Condição</span><strong>{LAMP_CONDITION_LABELS[form.lampCondition]}</strong></div>
              {form.description && <div className="summary-row"><span>Obs.</span><strong>{form.description}</strong></div>}
              {photoPreview && (
                <div className="summary-row summary-row-photo">
                  <span>Foto</span>
                  <strong><img src={photoPreview} alt="Foto do poste" className="summary-photo" /></strong>
                </div>
              )}
            </div>

            <div className="checkbox-group">
              <input type="checkbox" checked={isAdult} onChange={(e) => setIsAdult(e.target.checked)} />
              <label>Confirmo que sou maior de 18 anos</label>
            </div>
            <div className="checkbox-group">
              <input type="checkbox" checked={dataIsTrue} onChange={(e) => setDataIsTrue(e.target.checked)} />
              <label>Declaro que as informações fornecidas são verídicas</label>
            </div>
            <div className="checkbox-group">
              <input type="checkbox" checked={acceptsPrivacyPolicy} onChange={(e) => setAcceptsPrivacyPolicy(e.target.checked)} />
              <label>
                Li e concordo com o{' '}
                <a href="/privacidade" target="_blank" rel="noopener noreferrer"
                  style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 }}>
                  tratamento dos meus dados pessoais
                </a>
              </label>
            </div>

            {TURNSTILE_SITE_KEY && !IS_LOCALHOST && turnstileReady && (
              <div style={{ display: 'flex', justifyContent: 'center', margin: '16px 0' }}>
                <div ref={renderTurnstile} />
              </div>
            )}
          </>
        )}

        {/* ════════════════════════ Navegação ════════════════════════ */}
        <div className="wizard-nav">
          {step > 1 && (
            <button type="button" className="btn btn-secondary" onClick={goPrev}>
              <FontAwesomeIcon icon={faChevronLeft} /> Voltar
            </button>
          )}
          <div style={{ flex: 1 }} />
          {step < 4 ? (
            <button type="button" className="btn btn-primary" onClick={handleNext}>
              {STEP_LABELS[(step + 1) as Step]?.title || 'Próximo'} <FontAwesomeIcon icon={faChevronRight} />
            </button>
          ) : (
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading && <span className="spinner" />}
              {photoUploading ? 'Enviando foto...' : loading ? 'Enviando...' : 'Enviar solicitação'}
            </button>
          )}
        </div>

        <p className="wizard-progress">Etapa {step} de {totalSteps}</p>
      </form>
    </div>
  );
}